import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useEffect, useRef, useCallback } from 'react';
import { embedDashboard, EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { rem, rgba } from 'polished';
import styled from 'styled-components';
import { Alert } from '@unmind/design-system';
import { logDevOnlyInfo } from '../../App/logging';

const SUPERSET_URL = 'https://analytics.unmind.com';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
`;

const DashboardContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - ${rem(80)}); /* Account for title and margins */

  /* Force iframe to fill container */
  iframe {
    width: 100% !important;
    height: 100% !important;
    min-height: calc(100vh - ${rem(80)}) !important;
    border-radius: ${rem(12)};
    border: 1px solid ${({ theme }) => rgba(theme.colors.border.secondary, 0.2)};
  }
`;

export const SUPERSET_TOKEN = gql`
  query supersetToken {
    analytics {
      supersetToken {
        expiresAt
        token
      }
    }
  }
`;

interface TokenCache {
  token: string | null;
  expiresAt: number | null;
}

const globalTokenCache: TokenCache = { token: null, expiresAt: null };

/**
 * Clears the global token cache
 * Exported for testing purposes only
 */
export const clearTokenCache = () => {
  globalTokenCache.token = null;
  globalTokenCache.expiresAt = null;
};

export const SupersetDashboard = ({ dashboardId }: { dashboardId: string }) => {
  const [getSupersetToken, { error }] = useLazyQuery(SUPERSET_TOKEN);
  const embedRef = useRef<EmbeddedDashboard | null>(null);

  /**
   * Fetches guest token with caching and automatic refresh
   * Returns cached token if still valid (with 30 second buffer)
   * Otherwise fetches new token from GraphQL API
   * Uses module-level cache shared across all dashboard instances
   */
  const fetchGuestToken = useCallback(async (): Promise<string> => {
    // Check cache first (with 30 second buffer before expiry)
    if (
      globalTokenCache.token &&
      globalTokenCache.expiresAt &&
      Date.now() < globalTokenCache.expiresAt - 30000
    ) {
      logDevOnlyInfo('Using cached Superset token');

      return globalTokenCache.token;
    }
    try {
      logDevOnlyInfo('Fetching new Superset token...');

      // Fetch new token from GraphQL API
      const { data } = await getSupersetToken();

      if (!data?.analytics?.supersetToken?.token) {
        throw new Error('Failed to fetch Superset token');
      }

      const { token, expiresAt } = data.analytics.supersetToken;

      // Parse expiresAt and cache the token
      const expiryTimestamp = expiresAt
        ? new Date(expiresAt).getTime()
        : Date.now() + 300000; // Default 5 min

      globalTokenCache.token = token;
      globalTokenCache.expiresAt = expiryTimestamp;

      logDevOnlyInfo('Superset token obtained');

      return token;
    } catch (err) {
      console.error('Error fetching guest token:', err);
      throw err;
    }
  }, [getSupersetToken]);

  useEffect(() => {
    const mountPointId = `dashboard-${dashboardId}`;
    const mountPoint = document.getElementById(mountPointId) as HTMLElement;
    const embed = async () => {
      try {
        if (!mountPoint) return;

        if (embedRef.current) {
          mountPoint.innerHTML = '';
          embedRef.current = null;
        }

        const embedDashboardParams = {
          id: dashboardId, // given by the Superset embedding UI
          supersetDomain: SUPERSET_URL,
          mountPoint,
          fetchGuestToken,
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: true,
            hideTab: true,
          },
        };

        embedRef.current = await embedDashboard(embedDashboardParams);
      } catch (embedError) {
        console.error('Error embedding dashboard:', embedError);
      }
    };

    void embed();

    // Cleanup only on unmount
    return () => {
      if (mountPoint) {
        mountPoint.innerHTML = '';
      }
    };
  }, [fetchGuestToken, dashboardId]);

  if (error) {
    return (
      <AppContainer>
        <Alert
          variant="error"
          size="small"
          title={`An error has occurred while loading the selected dashboard. Please try again later.`}
        />
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <DashboardContainer id={`dashboard-${dashboardId}`} />
    </AppContainer>
  );
};
export default SupersetDashboard;
